import {
  ContainerCluster,
  useCreateContainerClusterMutation,
  useDeleteContainerClusterMutation,
  useUpdateContainerClusterMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseContainerClusterMutationProps = {}

export type UseContainerClusterMutationResult = {
  createContainerCluster: (containerCluster: Partial<ContainerCluster>, onSuccess: ((containerCluster: ContainerCluster) => void) | undefined) => void
  updateContainerCluster: (containerCluster: Partial<ContainerCluster>, onSuccess: (() => void) | undefined) => void
  deleteContainerCluster: (containerClusterId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useContainerClusterMutation = (props: UseContainerClusterMutationProps = {}): UseContainerClusterMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createContainerCluster, {isLoading: isCreating}] = useCreateContainerClusterMutation()
  const [_updateContainerCluster, {isLoading: isUpdating}] = useUpdateContainerClusterMutation()
  const [_deleteContainerCluster, {isLoading: isDeleting}] = useDeleteContainerClusterMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useContainerClusterUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.containercluster", "Container Cluster"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerClusterUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useContainerClusterUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.containercluster", "Container Cluster"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerClusterUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useContainerClusterUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.containercluster", "Container Cluster"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerClusterUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ContainerCluster>, onSuccess: ((containerCluster: ContainerCluster) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.containercluster", "Container Cluster") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.containercluster", "Container Cluster") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.containercluster", "Container Cluster") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((containerClusterId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteContainerCluster({id: containerClusterId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteContainerCluster, handleDeleteResult])

  const createContainerCluster = useCallback((containerCluster: Partial<ContainerCluster>, onSuccess: ((containerCluster: ContainerCluster) => void) | undefined) => {
    const promise = _createContainerCluster(containerCluster).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createContainerCluster, handleCreateResult])

  const updateContainerCluster = useCallback((containerCluster: Partial<ContainerCluster>, onSuccess: (() => void) | undefined) => {
    const promise = _updateContainerCluster(containerCluster).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateContainerCluster, handleUpdateResult])

  const deleteContainerCluster = useCallback((containerClusterId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.containercluster", "Container Cluster"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletecontainercluster.confirm", "Are you sure you want to delete this container cluster?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(containerClusterId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createContainerCluster: createContainerCluster,
    updateContainerCluster: updateContainerCluster,
    deleteContainerCluster: deleteContainerCluster,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
